import React, { useEffect } from 'react';
import queryString from 'query-string';
import { useRouter } from 'next/router';
import { Paging, Text, RectangleSkeleton, Flexbox, Grid } from '@morressier/ts';
import { SubmissionCard } from 'components/Cards';
import HomeSearchNoResults from 'components/Search/HomeSearchNoResults';
import { Container, PagingContainer } from '../style';
import pagingLink from '../PagingLink';
import { useSubmissions } from 'clients/internal/homeSearch';
import MetaTags from 'components/MetaTags';
import { logEvent } from 'utils/eventTracking';
import { ResultsText } from '../ResultsText';

export const SkeletonLoader = (): JSX.Element => {
  return (
    <Flexbox
      flexWrap="wrap"
      style={{ width: '100%', marginBottom: 10, borderRadius: '1rem' }}
    >
      <div style={{ width: '100%' }}>
        <RectangleSkeleton width="35%" height="1.75rem" className="mb1" />
      </div>
      <Flexbox
        justifyContent="space-between"
        style={{ width: '100%', marginBottom: 10 }}
      >
        <div style={{ width: '75%' }}>
          <RectangleSkeleton width="100%" height="1.5rem" />
          <RectangleSkeleton width="80%" height="1.5rem" className="mr1" />
          <RectangleSkeleton width="35%" height="1.75rem" className="mt2" />
        </div>
      </Flexbox>
      <RectangleSkeleton width="100%" height="1.5rem" className="mt4" />
    </Flexbox>
  );
};

const ITEMS_PER_PAGE = 10;
interface ISearchSubmissionsProps {
  inputTerm: string;
}

const SearchSubmissions = (props: ISearchSubmissionsProps): JSX.Element => {
  const router = useRouter();
  const { inputTerm } = props;
  const page = parseInt(router.query?.page as string) || 1;

  const {
    data: submissions,
    isLoading,
    hasError,
    error
  } = useSubmissions({
    inputTerm,
    page: page - 1,
    itemsPerPage: ITEMS_PER_PAGE
  });
  const totalPages = Math.ceil((submissions?.total || 0) / ITEMS_PER_PAGE);

  // TODO https://morressier.atlassian.net/browse/DISCO-682
  // if (!isLoading) {
  //   logEvent('SEARCH_RESULTS_LOADED', {
  //     inputTerm,
  //     contentType: 'SUBMISSION'
  //   });
  // }

  useEffect(() => {
    const { scrollTo } = router.query;
    if (submissions && scrollTo && submissions.total !== 0) {
      const element = document?.querySelector(`#${scrollTo}`);
      element?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [submissions]);

  if (!isLoading && submissions?.total === 0) {
    return (
      <Container>
        <MetaTags title={`No Search result for ${inputTerm}`} />
        <HomeSearchNoResults key="events-no-results" searchTerm={inputTerm} />
      </Container>
    );
  }

  const logSubmissionCardEvent = async (
    submissionId: string,
    index: number
  ) => {
    logEvent('SEARCH_RESULTS_RESULT_CLICKED', {
      resultLocation: index,
      page,
      searchTab: 'SUBMISSIONS',
      typeOfCard: 'SUBMISSION',
      ID: submissionId
    });
  };

  const renderSubmissionCard = () => {
    return submissions?.data?.map((submission, index) => {
      const params = queryString.stringify({
        ...router.query,
        scrollTo: `sub${submission.id}`
      });
      const url = encodeURIComponent(`${origin}${router.pathname}?${params}`);
      return (
        <SubmissionCard
          key={submission.id}
          submission={submission}
          submissionId={submission.id}
          currentPath={url}
          logEvent={() => logSubmissionCardEvent(submission.id, index + 1)}
        />
      );
    });
  };

  if (hasError && !isLoading)
    return (
      <Container>
        <div>Error searching for submissions: {error?.message}</div>
      </Container>
    );

  // Page meta data
  const pageTitle = `Search Submissions${inputTerm && ' - '}${inputTerm}${
    page && ' - '
  }${page}`;
  const paginated = {
    router,
    lastPage: Math.ceil((submissions?.total || 0) / ITEMS_PER_PAGE)
  };

  return (
    <Container>
      <MetaTags title={pageTitle} paginated={paginated} />
      <ResultsText
        totalResults={submissions?.total?.toLocaleString() || ''}
        type="Research Submissions"
        searchTerm={inputTerm}
      />

      <Grid xsmall={1} gap="1rem">
        {isLoading ? (
          <>
            <SkeletonLoader />
            <SkeletonLoader />
            <SkeletonLoader />
          </>
        ) : (
          <>{renderSubmissionCard()}</>
        )}
      </Grid>

      <PagingContainer key="submissions-paging-container">
        <Paging
          key="submissions-paging"
          currentPage={page}
          totalPages={totalPages}
          previousButtonText="Prev"
          nextButtonText="Next"
          renderLink={pagingLink('/search/research', inputTerm, page)}
        />
      </PagingContainer>
    </Container>
  );
};

export default SearchSubmissions;
